import { InternalFormValues } from './types';
import { UpdateSelfDTO, User } from '../../../../data/models/user';
import dayjs from 'dayjs';

const normalizeBoolean = (
	value: string | boolean | undefined
): boolean | undefined => {
	if (typeof value === 'boolean') {
		return value;
	}
	if (value === 'yes') {
		return true;
	}
	if (value === 'no') {
		return false;
	}
	return undefined;
};

const sanitizeString = (value?: string): string | undefined => {
	if (typeof value !== 'string') {
		return undefined;
	}
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : undefined;
};

const sanitizeAddress = (address?: InternalFormValues['homeAddress']) => {
	if (!address) {
		return undefined;
	}
	const cleaned = {
		line1: sanitizeString(address.line1),
		line2: sanitizeString(address.line2),
		city: sanitizeString(address.city),
		state: sanitizeString(address.state),
		zip: sanitizeString(address.zip),
	};
	const hasValues = Object.values(cleaned).some(
		(value) => value !== undefined
	);
	return hasValues ? cleaned : undefined;
};

export const createInternalDefaultValues = (): InternalFormValues => ({
	firstName: '',
	lastName: '',
	preferredName: '',
	preferredPronouns: '',
	email: '',
	personalEmail: '',
	phone: '',
	phoneCarrier: '',
	isIphone: '',
	smsTextReady: '',
	hasWhatsapp: '',
	whatsappNumber: '',
	hasWechat: '',
	wechatNumber: '',
	emergencyContactFirstName: '',
	emergencyContactLastName: '',
	emergencyContactPhone: '',
	homeAddress: {
		line1: '',
		line2: '',
		city: '',
		state: '',
		zip: '',
	},
	shippingIsSameAsHome: false,
	shippingAddress: {
		line1: '',
		line2: '',
		city: '',
		state: '',
		zip: '',
	},
	role: '',
	websiteUrl: '',
	instagramHandle: '',
	healthInsurance: '',
	professionalLicenses: '',
	dietaryRestrictions: '',
	photographyConsent: '',
	shirtSize: '',
	pantSize: '',
	shoeSize: '',
	biopoc: '',
	homeAirport: '',
	preferredSeating: '',
	dateOfBirth: '',
	tsaPrecheck: '',
	globalEntry: '',
	passportNumber: '',
	passportIssuingCountry: '',
	passportExpirationDate: '',
	noPassport: false,
	preferredAirlines: [],
	deltaMileage: '',
	unitedMileage: '',
	driversLicenseNumber: '',
	driversLicenseState: '',
	noDriversLicense: false,
	hasVehicle: '',
	willingToUseVehicleForProduction: '',
	vehicleType: '',
	vehicleLocation: '',
});

export const mapUserToInternalFormValues = (user: User): InternalFormValues => {
	const defaultValues = createInternalDefaultValues();
	const userProfile: any = (user as any)?.userProfile || {};

	const userAny = user as any;
	return {
		...defaultValues,
		firstName: userAny?.firstName || '',
		lastName: userAny?.lastName || '',
		preferredName: userAny?.preferredName || '',
		preferredPronouns: userAny?.preferredPronouns || '',
		email: userAny?.email || '',
		personalEmail: userAny?.personalEmail || '',
		phone: userAny?.phone || '',
		role: userAny?.role || '',
		phoneCarrier: userProfile?.phoneCarrier || '',
		isIphone:
			userProfile?.isIphone === true
				? 'yes'
				: userProfile?.isIphone === false
				? 'no'
				: '',
		smsTextReady:
			userProfile?.smsTextReady === true
				? 'yes'
				: userProfile?.smsTextReady === false
				? 'no'
				: '',
		hasWhatsapp:
			userProfile?.hasWhatsapp === true
				? 'yes'
				: userProfile?.hasWhatsapp === false
				? 'no'
				: '',
		whatsappNumber: userProfile?.whatsappNumber || '',
		hasWechat:
			userProfile?.hasWechat === true
				? 'yes'
				: userProfile?.hasWechat === false
				? 'no'
				: '',
		wechatNumber: userProfile?.wechatNumber || '',
		emergencyContactFirstName: userProfile?.emergencyContactFirstName || '',
		emergencyContactLastName: userProfile?.emergencyContactLastName || '',
		emergencyContactPhone: userProfile?.emergencyContactPhone || '',
		homeAddress: {
			line1: userProfile?.homeAddress?.line1 || '',
			line2: userProfile?.homeAddress?.line2 || '',
			city: userProfile?.homeAddress?.city || '',
			state: userProfile?.homeAddress?.state || '',
			zip: userProfile?.homeAddress?.zip || '',
		},
		shippingIsSameAsHome:
			Boolean(userProfile?.shippingAddress?.sameAsHomeAddress) || false,
		shippingAddress: {
			line1: userProfile?.shippingAddress?.line1 || '',
			line2: userProfile?.shippingAddress?.line2 || '',
			city: userProfile?.shippingAddress?.city || '',
			state: userProfile?.shippingAddress?.state || '',
			zip: userProfile?.shippingAddress?.zip || '',
		},
		websiteUrl: userProfile?.websiteUrl || '',
		instagramHandle: userProfile?.instagramHandle || '',
		healthInsurance:
			userProfile?.healthInsurance === true
				? 'yes'
				: userProfile?.healthInsurance === false
				? 'no'
				: '',
		professionalLicenses: userProfile?.professionalLicenses || '',
		dietaryRestrictions: userProfile?.dietaryRestrictions || '',
		photographyConsent:
			userProfile?.photographyConsent === true
				? 'yes'
				: userProfile?.photographyConsent === false
				? 'no'
				: '',
		shirtSize: userProfile?.shirtSize || '',
		pantSize: userProfile?.pantSize || '',
		shoeSize: userProfile?.shoeSize || '',
		biopoc:
			userProfile?.biopoc === true
				? 'yes'
				: userProfile?.biopoc === false
				? 'no'
				: '',
		homeAirport: userProfile?.homeAirport || '',
		preferredSeating: userProfile?.preferredSeating || '',
		dateOfBirth: userProfile?.dateOfBirth || '',
		tsaPrecheck: userProfile?.tsaPrecheck || '',
		globalEntry: userProfile?.globalEntry || '',
		passportNumber: userProfile?.passportNumber || '',
		passportIssuingCountry: userProfile?.passportIssuingCountry || '',
		passportExpirationDate: userProfile?.passportExpirationDate || '',
		noPassport: Boolean(userProfile?.noPassport) || false,
		preferredAirlines: userProfile?.preferredAirlines || [],
		deltaMileage: userProfile?.deltaMileage || '',
		unitedMileage: userProfile?.unitedMileage || '',
		driversLicenseNumber: userProfile?.driversLicenseNumber || '',
		driversLicenseState: userProfile?.driversLicenseState || '',
		noDriversLicense: Boolean(userProfile?.noDriversLicense) || false,
		hasVehicle:
			userProfile?.hasVehicle === true
				? 'yes'
				: userProfile?.hasVehicle === false
				? 'no'
				: '',
		willingToUseVehicleForProduction:
			userProfile?.willingToUseVehicleForProduction === true
				? 'yes'
				: userProfile?.willingToUseVehicleForProduction === false
				? 'no'
				: '',
		vehicleType: userProfile?.vehicleType || '',
		vehicleLocation: userProfile?.vehicleLocation || '',
	};
};

const removeEmptyAddress = (updateData: UpdateSelfDTO) => {
	['homeAddress', 'shippingAddress'].forEach((key) => {
		const value = updateData[key as keyof UpdateSelfDTO];
		if (value && typeof value === 'object') {
			const entries = Object.entries(value as Record<string, unknown>);
			const filtered = entries.reduce<Record<string, unknown>>(
				(acc, [k, v]) => {
					if (v !== undefined && v !== '') {
						acc[k] = v;
					}
					return acc;
				},
				{}
			);
			if (Object.keys(filtered).length === 0) {
				delete updateData[key as keyof UpdateSelfDTO];
			} else {
				updateData[key as keyof UpdateSelfDTO] = filtered as any;
			}
		}
	});
};

export const buildInternalSavePayload = (
	values: InternalFormValues,
	helpers: { isFieldDirty: (fieldPath: string) => boolean }
): UpdateSelfDTO | null => {
	const updateData: UpdateSelfDTO = {};
	const { isFieldDirty } = helpers;

	if (isFieldDirty('firstName'))
		updateData.firstName = sanitizeString(values.firstName);
	if (isFieldDirty('lastName'))
		updateData.lastName = sanitizeString(values.lastName);
	if (isFieldDirty('preferredName'))
		updateData.preferredName = sanitizeString(values.preferredName);
	if (isFieldDirty('preferredPronouns'))
		updateData.preferredPronouns = sanitizeString(values.preferredPronouns);
	if (isFieldDirty('personalEmail'))
		updateData.personalEmail = sanitizeString(values.personalEmail);
	if (isFieldDirty('phone')) updateData.phone = sanitizeString(values.phone);
	if (isFieldDirty('phoneCarrier'))
		updateData.phoneCarrier = sanitizeString(values.phoneCarrier);
	if (isFieldDirty('isIphone'))
		updateData.isIphone = normalizeBoolean(values.isIphone) ?? undefined;
	if (isFieldDirty('smsTextReady'))
		updateData.smsTextReady =
			normalizeBoolean(values.smsTextReady) ?? undefined;
	if (isFieldDirty('hasWhatsapp'))
		updateData.hasWhatsapp =
			normalizeBoolean(values.hasWhatsapp) ?? undefined;
	if (isFieldDirty('whatsappNumber'))
		updateData.whatsappNumber = sanitizeString(values.whatsappNumber);
	if (isFieldDirty('hasWechat'))
		updateData.hasWechat = normalizeBoolean(values.hasWechat) ?? undefined;
	if (isFieldDirty('wechatNumber'))
		updateData.wechatNumber = sanitizeString(values.wechatNumber);
	if (isFieldDirty('emergencyContactFirstName'))
		updateData.emergencyContactFirstName = sanitizeString(
			values.emergencyContactFirstName
		);
	if (isFieldDirty('emergencyContactLastName'))
		updateData.emergencyContactLastName = sanitizeString(
			values.emergencyContactLastName
		);
	if (isFieldDirty('emergencyContactPhone'))
		updateData.emergencyContactPhone = sanitizeString(
			values.emergencyContactPhone
		);

	if (
		isFieldDirty('homeAddress.line1') ||
		isFieldDirty('homeAddress.line2') ||
		isFieldDirty('homeAddress.city') ||
		isFieldDirty('homeAddress.state') ||
		isFieldDirty('homeAddress.zip')
	) {
		updateData.homeAddress = sanitizeAddress(values.homeAddress);
	}

	if (
		isFieldDirty('shippingAddress.line1') ||
		isFieldDirty('shippingAddress.line2') ||
		isFieldDirty('shippingAddress.city') ||
		isFieldDirty('shippingAddress.state') ||
		isFieldDirty('shippingAddress.zip') ||
		isFieldDirty('shippingIsSameAsHome')
	) {
		updateData.shippingAddress = {
			...sanitizeAddress(values.shippingAddress),
			sameAsHomeAddress: Boolean(values.shippingIsSameAsHome),
		};
	}

	if (isFieldDirty('role')) updateData.role = sanitizeString(values.role);
	if (isFieldDirty('websiteUrl'))
		updateData.websiteUrl = sanitizeString(values.websiteUrl);
	if (isFieldDirty('instagramHandle'))
		updateData.instagramHandle = sanitizeString(values.instagramHandle);
	if (isFieldDirty('healthInsurance'))
		updateData.healthInsurance = normalizeBoolean(values.healthInsurance);
	if (isFieldDirty('professionalLicenses'))
		updateData.professionalLicenses = sanitizeString(
			values.professionalLicenses
		);
	if (isFieldDirty('dietaryRestrictions'))
		updateData.dietaryRestrictions = sanitizeString(
			values.dietaryRestrictions
		);
	if (isFieldDirty('photographyConsent'))
		updateData.photographyConsent = normalizeBoolean(
			values.photographyConsent
		);
	if (isFieldDirty('shirtSize'))
		updateData.shirtSize = sanitizeString(values.shirtSize);
	if (isFieldDirty('pantSize'))
		updateData.pantSize = sanitizeString(values.pantSize);
	if (isFieldDirty('shoeSize'))
		updateData.shoeSize = sanitizeString(values.shoeSize);
	if (isFieldDirty('biopoc'))
		updateData.biopoc = normalizeBoolean(values.biopoc);
	if (isFieldDirty('homeAirport'))
		updateData.homeAirport = sanitizeString(values.homeAirport);
	if (isFieldDirty('preferredSeating'))
		updateData.preferredSeating = sanitizeString(values.preferredSeating);
	if (isFieldDirty('dateOfBirth'))
		updateData.dateOfBirth = values.dateOfBirth
			? dayjs(values.dateOfBirth).format('YYYY-MM-DD')
			: undefined;
	if (isFieldDirty('tsaPrecheck'))
		updateData.tsaPrecheck = sanitizeString(values.tsaPrecheck);
	if (isFieldDirty('globalEntry'))
		updateData.globalEntry = sanitizeString(values.globalEntry);
	if (isFieldDirty('passportNumber'))
		updateData.passportNumber = sanitizeString(values.passportNumber);
	if (isFieldDirty('passportIssuingCountry'))
		updateData.passportIssuingCountry = sanitizeString(
			values.passportIssuingCountry
		);
	if (isFieldDirty('passportExpirationDate'))
		updateData.passportExpirationDate = values.passportExpirationDate
			? dayjs(values.passportExpirationDate).format('YYYY-MM-DD')
			: undefined;
	if (isFieldDirty('noPassport')) updateData.noPassport = values.noPassport;
	if (isFieldDirty('preferredAirlines'))
		updateData.preferredAirlines = values.preferredAirlines;
	if (isFieldDirty('deltaMileage'))
		updateData.deltaMileage = sanitizeString(values.deltaMileage);
	if (isFieldDirty('unitedMileage'))
		updateData.unitedMileage = sanitizeString(values.unitedMileage);
	if (isFieldDirty('driversLicenseNumber'))
		updateData.driversLicenseNumber = sanitizeString(
			values.driversLicenseNumber
		);
	if (isFieldDirty('driversLicenseState'))
		updateData.driversLicenseState = sanitizeString(
			values.driversLicenseState
		);
	if (isFieldDirty('noDriversLicense'))
		updateData.noDriversLicense = values.noDriversLicense;
	if (isFieldDirty('hasVehicle'))
		updateData.hasVehicle =
			normalizeBoolean(values.hasVehicle) ?? undefined;
	if (isFieldDirty('willingToUseVehicleForProduction'))
		updateData.willingToUseVehicleForProduction =
			normalizeBoolean(values.willingToUseVehicleForProduction) ??
			undefined;
	if (isFieldDirty('vehicleType'))
		updateData.vehicleType = sanitizeString(values.vehicleType);
	if (isFieldDirty('vehicleLocation'))
		updateData.vehicleLocation = sanitizeString(values.vehicleLocation);

	removeEmptyAddress(updateData);

	return Object.keys(updateData).length > 0 ? updateData : null;
};

export const buildInternalSubmitPayload = (
	values: InternalFormValues
): UpdateSelfDTO => ({
	firstName: sanitizeString(values.firstName),
	lastName: sanitizeString(values.lastName),
	preferredName: sanitizeString(values.preferredName),
	preferredPronouns: sanitizeString(values.preferredPronouns),
	personalEmail: sanitizeString(values.personalEmail),
	phone: sanitizeString(values.phone),
	phoneCarrier: sanitizeString(values.phoneCarrier),
	isIphone: normalizeBoolean(values.isIphone) ?? undefined,
	smsTextReady: normalizeBoolean(values.smsTextReady) ?? undefined,
	hasWhatsapp: normalizeBoolean(values.hasWhatsapp) ?? undefined,
	whatsappNumber: sanitizeString(values.whatsappNumber),
	hasWechat: normalizeBoolean(values.hasWechat) ?? undefined,
	wechatNumber: sanitizeString(values.wechatNumber),
	emergencyContactFirstName: sanitizeString(values.emergencyContactFirstName),
	emergencyContactLastName: sanitizeString(values.emergencyContactLastName),
	emergencyContactPhone: sanitizeString(values.emergencyContactPhone),
	homeAddress: sanitizeAddress(values.homeAddress),
	shippingAddress: {
		...sanitizeAddress(values.shippingAddress),
		sameAsHomeAddress: Boolean(values.shippingIsSameAsHome),
	},
	role: sanitizeString(values.role),
	websiteUrl: sanitizeString(values.websiteUrl),
	instagramHandle: sanitizeString(values.instagramHandle),
	healthInsurance: normalizeBoolean(values.healthInsurance) ?? undefined,
	professionalLicenses: sanitizeString(values.professionalLicenses),
	dietaryRestrictions: sanitizeString(values.dietaryRestrictions),
	photographyConsent:
		normalizeBoolean(values.photographyConsent) ?? undefined,
	shirtSize: sanitizeString(values.shirtSize),
	pantSize: sanitizeString(values.pantSize),
	shoeSize: sanitizeString(values.shoeSize),
	biopoc: normalizeBoolean(values.biopoc) ?? undefined,
	homeAirport: sanitizeString(values.homeAirport),
	preferredSeating: sanitizeString(values.preferredSeating),
	dateOfBirth: values.dateOfBirth || undefined,
	tsaPrecheck: sanitizeString(values.tsaPrecheck),
	globalEntry: sanitizeString(values.globalEntry),
	passportNumber: sanitizeString(values.passportNumber),
	passportIssuingCountry: sanitizeString(values.passportIssuingCountry),
	passportExpirationDate: values.passportExpirationDate || undefined,
	noPassport: values.noPassport,
	preferredAirlines: values.preferredAirlines,
	deltaMileage: sanitizeString(values.deltaMileage),
	unitedMileage: sanitizeString(values.unitedMileage),
	driversLicenseNumber: sanitizeString(values.driversLicenseNumber),
	driversLicenseState: sanitizeString(values.driversLicenseState),
	noDriversLicense: values.noDriversLicense,
	hasVehicle: normalizeBoolean(values.hasVehicle) ?? undefined,
	willingToUseVehicleForProduction:
		normalizeBoolean(values.willingToUseVehicleForProduction) ?? undefined,
	vehicleType: sanitizeString(values.vehicleType),
	vehicleLocation: sanitizeString(values.vehicleLocation),
	status: 'SUBSCRIBED',
});
